import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { getAllNews } from "../services/newsServices";
import { getActivePopup } from "../services/popupsService";
import HeroSection from '../components/home/HeroSection';
import HistorySection from '../components/home/HistorySection';
import NewsSection from '../components/home/NewsSection';
import PopupModal from '../components/common/PopupModal';

export default function Home() {
  const [approvedNews, setApprovedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allNews, setAllNews] = useState([]);
  const [error, setError] = useState(null);
  const [activePopup, setActivePopup] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastPopupIdRef = useRef(null);
  
  // Verificar si el usuario es admin
  const isAdmin = user?.role === 'admin';
  
  // Optimized data fetching with error handling
  useEffect(() => {
    let isMounted = true;
    
    async function fetchApprovedNews() {
      try {
        setLoading(true);
        const newsData = await getAllNews(user);
        
        if (isMounted) {
          setAllNews(newsData);
          const approved = newsData.filter(newsItem => newsItem.status === 'approved');
          setApprovedNews(approved.slice(0, 3));
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching news:", err);
        if (isMounted) {
          setError("Error al cargar noticias aprobadas");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    fetchApprovedNews();
    
    return () => {
      isMounted = false;
    };
  }, [user]);

  // Cargar pop-up activo solo para admin y mostrarlo UNA SOLA VEZ al iniciar sesión
  useEffect(() => {
    if (!isAdmin || !user) {
      setActivePopup(null);
      setShowPopup(false);
      lastPopupIdRef.current = null;
      return;
    }

    // Verificar si ya se mostró el pop-up en esta sesión
    const popupAlreadyShown = sessionStorage.getItem('admin_popup_shown') === 'true';

    let isMounted = true;

    async function fetchActivePopup() {
      try {
        const popup = await getActivePopup();
        
        if (!isMounted) return;

        if (popup && popup.imageUrl && !popupAlreadyShown) {
          // Mostrar el pop-up si no se ha mostrado en esta sesión
          setActivePopup(popup);
          setShowPopup(true);
          lastPopupIdRef.current = popup.id;
          // Marcar que ya se mostró en esta sesión
          sessionStorage.setItem('admin_popup_shown', 'true');
        } else {
          // Ya se mostró o no hay pop-up activo
          if (isMounted) {
            setActivePopup(null);
            setShowPopup(false);
            if (!popup || !popup.imageUrl) {
              lastPopupIdRef.current = null;
            }
          }
        }
      } catch (err) {
        // Error silencioso
        if (isMounted) {
          setActivePopup(null);
          setShowPopup(false);
        }
      }
    }

    // Cargar y mostrar pop-up activo SOLO una vez por sesión
    fetchActivePopup();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, user]);

  // Handler with memoization
  const handleReadMore = useMemo(() => (id) => {
    navigate(`/news/${id}`, { state: { news: allNews } });
  }, [navigate, allNews]);

  return (
    <>
      {/* Pop-up modal para admin - solo se muestra una vez al iniciar sesión */}
      {isAdmin && showPopup && activePopup && (
        <PopupModal
          popup={activePopup}
          onClose={() => {
            setShowPopup(false);
          }}
        />
      )}

      <div className="flex flex-col min-h-screen w-full max-w-full px-4 md:px-8 py-4 md:py-6">
        <HeroSection />
        <HistorySection />
        <NewsSection 
          news={approvedNews}
          loading={loading}
          error={error}
          onReadMore={handleReadMore}
        />
      </div>
    </>
  );
}